// src/mobile_pages/meeting/MobileScheduleNew.jsx
// 모바일 전용 일정 등록/수정 폼

import React, { useState, useEffect } from "react";
import { Calendar, Clock, Users, FileText, X } from "lucide-react";
import api from "../../api/axios";
import { scheduleApi } from "../../api/schedules";
import ReferenceModal from "../../components/approval/ReferenceModal";

const scopeOptions = [
    { key: "project", label: "프로젝트" },
    { key: "company", label: "회사" },
    { key: "personal", label: "개인" },
];

export default function MobileScheduleNew({
    projectId,
    companyId,
    initial,
    mode = "create",
    onSaved,
    onClose,
}) {
    // === scope 선택 (프로젝트 / 회사 / 개인) ===
    const [scope, setScope] = useState(initial?.scope ?? "personal");

    // 날짜, 시간, form
    const [date, setDate] = useState(initial?.start?.slice(0, 10) || "");
    const [time, setTime] = useState(initial?.start?.slice(11, 16) || "");
    const [form, setForm] = useState({
        title: initial?.title || "",
        memo: initial?.memo || "",
    });

    const [users, setUsers] = useState([]);
    const [participantIds, setParticipantIds] = useState(
        initial?.participants?.map((p) => Number(p.id)) || []
    );
    const [modalOpen, setModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const onChange = (e) =>
        setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    // ===================== 사용자 목록 로드 =====================
    useEffect(() => {
        (async () => {
            try {
                if (scope === "company") {
                    setUsers([]);
                    return;
                }

                let res;
                if (scope === "project") {
                    res = await api.get(`/project/${projectId}/users-without-me/`);
                } else {
                    setUsers([]);
                    return;
                }

                const members = res.data?.results ?? res.data ?? [];
                setUsers(members);
            } catch (err) {
                console.error("사용자 목록 조회 실패", err);
            }
        })();
    }, [scope, projectId, companyId]);

    // ===================== 저장 =====================
    const submit = async () => {
        if (!form.title.trim()) return alert("제목을 입력해주세요.");
        if (!date) return alert("날짜를 선택해주세요.");
        if (!time) return alert("시간을 선택해주세요.");

        const isoStart = new Date(`${date}T${time}:00`).toISOString();

        const payload = {
            title: form.title,
            start: isoStart,
            memo: form.memo,
            scope,
            participant_ids: scope === "personal" ? [] : participantIds,
            project: projectId,
            company: scope === "company" ? companyId : null,
        };

        try {
            setSaving(true);
            const { data } =
                mode === "create"
                    ? await scheduleApi.create(payload)
                    : await scheduleApi.update(initial.id, payload);

            onSaved?.(data);
        } catch (err) {
            console.error("저장 실패", err);
            alert("저장에 실패했습니다.");
        } finally {
            setSaving(false);
        }
    };

    const userById = new Map(users.map((u) => [Number(u.id), u]));

    // Styles
    const labelStyle = "flex items-center gap-1.5 text-[13px] font-bold text-slate-400 mb-2";
    const inputContainerStyle = "bg-[#F2F4F6] rounded-2xl p-1"; // 연한 회색 박스
    const inputStyle = "w-full bg-transparent border-0 px-3 py-3 text-[16px] outline-none placeholder:text-slate-400 font-medium";

    return (
        <div className="flex flex-col h-full bg-white">
            {/* ======================== HEADER ======================== */}
            <header className="flex-none px-6 pt-6 pb-4 bg-white">
                <div className="flex items-start justify-between">
                    <div>
                        <h2 className="text-[24px] font-bold text-slate-900 leading-tight">
                            {mode === "create" ? "일정 등록" : "일정 수정"}
                        </h2>
                        <p className="text-[14px] text-slate-500 mt-1">
                            일정 정보를 입력해주세요.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center active:scale-95 transition-transform"
                    >
                        <X size={18} className="text-slate-500" />
                    </button>
                </div>
            </header>

            {/* ======================== CONTENT ======================== */}
            <div className="flex-1 overflow-y-auto px-6 pb-10 space-y-6">

                {/* 탭 (Segment Control) */}
                <div className="bg-[#F2F4F6] rounded-[18px] p-1.5 flex text-[14px] font-semibold">
                    {scopeOptions.map((opt) => {
                        const active = scope === opt.key;
                        const locked = mode === "edit";
                        return (
                            <button
                                key={opt.key}
                                disabled={locked}
                                onClick={() => {
                                    if (locked) return;
                                    setScope(opt.key);
                                    setParticipantIds([]);
                                }}
                                className={`flex-1 py-2.5 rounded-[14px] transition-all ${active
                                    ? "bg-white text-slate-900 shadow-sm"
                                    : "text-slate-400"
                                    } ${locked ? "cursor-default" : ""}`}
                            >
                                {opt.label}
                            </button>
                        )
                    })}
                </div>

                {/* 폼 카드 컨테이너 */}
                <div className="space-y-6">
                    {/* 제목 */}
                    <div>
                        <label className={labelStyle}>
                            <FileText size={14} strokeWidth={2.5} /> 제목
                        </label>
                        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden focus-within:ring-2 focus-within:ring-blue-100 focus-within:border-blue-400 transition-all">
                            <input
                                name="title"
                                className="w-full px-4 py-3.5 text-[16px] outline-none placeholder:text-slate-300 font-medium text-slate-900"
                                value={form.title}
                                onChange={onChange}
                                placeholder="일정 제목을 입력하세요"
                            />
                        </div>
                    </div>

                    {/* 일시 */}
                    <div>
                        <label className={labelStyle}>
                            <Calendar size={14} strokeWidth={2.5} /> 일시
                        </label>
                        <div className="flex flex-wrap gap-2">
                            <div className="flex-1 min-w-[140px] bg-white border border-slate-200 rounded-2xl flex items-center px-4 py-3.5 focus-within:ring-2 focus-within:ring-blue-100 focus-within:border-blue-400 transition-all">
                                <input
                                    type="date"
                                    className="w-full bg-transparent border-0 text-[15px] outline-none font-medium text-slate-900 font-nums"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                />
                            </div>
                            <div className="flex-1 min-w-[110px] bg-white border border-slate-200 rounded-2xl flex items-center px-4 py-3.5 focus-within:ring-2 focus-within:ring-blue-100 focus-within:border-blue-400 transition-all relative">
                                <input
                                    type="time"
                                    className="w-full bg-transparent border-0 text-[15px] outline-none font-medium text-slate-900 font-nums z-10"
                                    value={time}
                                    onChange={(e) => setTime(e.target.value)}
                                />
                                <Clock size={16} className="absolute right-4 text-slate-400 pointer-events-none" />
                            </div>
                        </div>
                    </div>

                    {/* 참여자 (개인이 아닐 때만) */}
                    {scope !== "personal" && (
                        <div>
                            <label className={labelStyle}>
                                <Users size={14} strokeWidth={2.5} /> 참여자
                            </label>

                            {/* 선택 버튼 */}
                            <button
                                type="button"
                                onClick={() => setModalOpen(true)}
                                className="w-full py-3.5 bg-blue-50 text-blue-600 rounded-2xl text-[15px] font-bold active:scale-[0.99] transition-transform mb-3"
                            >
                                + 참여자 추가
                            </button>

                            {/* 칩 목록 */}
                            {participantIds.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {participantIds.map((id) => {
                                        const u = userById.get(Number(id));
                                        const name = u ? `${u.last_name}${u.first_name}` : `ID: ${id}`;
                                        return (
                                            <span key={id} className="inline-flex items-center gap-1 pl-3 pr-2 py-1.5 bg-slate-100 rounded-full text-[13px] font-medium text-slate-700">
                                                {name}
                                                <button
                                                    onClick={() => setParticipantIds((prev) => prev.filter((x) => x !== id))}
                                                    className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-slate-200 text-slate-400"
                                                >
                                                    <X size={12} strokeWidth={3} />
                                                </button>
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* 메모 */}
                    <div>
                        <label className={labelStyle}>
                            <FileText size={14} strokeWidth={2.5} /> 메모
                        </label>
                        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden focus-within:ring-2 focus-within:ring-blue-100 focus-within:border-blue-400 transition-all p-1">
                            <textarea
                                name="memo"
                                className="w-full px-3 py-2.5 text-[15px] outline-none placeholder:text-slate-300 font-medium text-slate-900 resize-none min-h-[120px]"
                                value={form.memo}
                                onChange={onChange}
                                placeholder="메모를 입력하세요 (선택사항)"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* ======================== FOOTER ======================== */}
            <footer className="flex-none px-6 py-5 bg-white border-t border-slate-100 safe-area-bottom">
                <div className="flex gap-3 h-[52px]">
                    <button
                        onClick={onClose}
                        className="flex-1 rounded-2xl bg-slate-100 text-slate-600 text-[16px] font-bold active:bg-slate-200 transition-colors"
                    >
                        취소
                    </button>
                    <button
                        onClick={submit}
                        disabled={saving}
                        className="flex-[2] rounded-2xl bg-blue-600 text-white text-[16px] font-bold shadow-lg shadow-blue-500/30 active:bg-blue-700 transition-all disabled:opacity-50 disabled:shadow-none"
                    >
                        {saving ? "저장 중..." : "등록"}
                    </button>
                </div>
            </footer>

            {/* ======================== MODAL ======================== */}
            <ReferenceModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                users={users}
                selectedIds={participantIds}
                scheduleType={scope}
                companyId={companyId}
                onChange={setParticipantIds}
                onSave={(ids, detailUsers) => {
                    setParticipantIds(ids);
                    const map = new Map(users.map((u) => [u.id, u]));
                    detailUsers.forEach((u) => map.set(u.id, u));
                    setUsers([...map.values()]);
                    setModalOpen(false);
                    return true;
                }}
                name="참여자"
            />
        </div>
    );
}
